import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ShieldAlert } from 'lucide-react';
import { bypassTaskSignature } from '../../services/api';
import type { SignatureBypassReason } from '../../services/api/types.gen';
import { useStore } from '../../store';
import {
  getBusinessErrorMessage,
  getConflictCode,
} from '../../utils/apiErrorDetail';
import type { TaskHeaderSecondaryAction } from '../task/TaskHeaderControls';
import { selectAttestationForFamily } from './attestations';
import type { ESignatureCredentialInput } from './esignatureCredentialApi';
import { useActiveAttestations } from './useActiveAttestations';
import { BypassSignatureDialog } from './BypassSignatureDialog';

interface UseSignatureBypassActionArgs {
  taskId: string | null | undefined;
  /** From `useTaskPermissions`. Mirrors the backend gate; see TaskDetailPanel. */
  canBypass: boolean;
  /** Refetch the task, its signatures and its bypasses after a successful bypass. */
  onBypassed: () => void | Promise<void>;
  /**
   * Mirror the failure into the panel's own error surface. The dialog already
   * shows the message and stays open; panels that also keep a banner want it
   * after the dialog is dismissed. Called with `null` when an attempt starts.
   */
  onError?: (message: string | null) => void;
  /**
   * The server refused because the chart still has OASIS CMS fatals. The
   * dialog closes so the panel can show where to fix them.
   */
  onOasisBlocked?: () => void;
}

interface UseSignatureBypassAction {
  /** Spread into the panel's `secondaryActions`; empty when the actor cannot bypass. */
  actionItems: TaskHeaderSecondaryAction[];
  /** Render alongside the panel's other dialogs. */
  dialog: React.ReactNode;
}

/**
 * The bypass ceremony (ROG-3269), packaged so every task type can offer it.
 *
 * A departed clinician leaves orders and misc tasks stuck exactly as they leave
 * visits stuck — the backend, the audit trail and both PDFs have always covered
 * all four task types. Only the entrance was visit-shaped. Rather than copy the
 * visit panel's sixty lines of state, deferral and error handling into three
 * more places, the whole action lives here: the panels contribute the task, the
 * permission and what to refetch.
 */
export function useSignatureBypassAction({
  taskId,
  canBypass,
  onBypassed,
  onError,
  onOasisBlocked,
}: UseSignatureBypassActionArgs): UseSignatureBypassAction {
  const user = useStore(state => state.user);
  const { attestations } = useActiveAttestations();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const frameRef = useRef<number | null>(null);

  // A bypass applies to any task type, so its family is selected directly
  // rather than resolved from the task (see resolveAttestationFamily).
  const attestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(attestations, 'signature_bypass');
  }, [attestations]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    []
  );

  const handleConfirm = useCallback(
    async (
      credential: ESignatureCredentialInput,
      reason: SignatureBypassReason,
      comments: string | null
    ): Promise<string | null> => {
      if (!taskId || !attestation) return null;

      try {
        setIsLoading(true);
        onError?.(null);
        // The generated client does not throw by default — a failure arrives as
        // `result.error`, so a try/catch alone would let it pass silently.
        const result = await bypassTaskSignature({
          path: { task_id: taskId },
          body: {
            ...credential,
            attestation_version: attestation.version,
            reason,
            comments,
          },
        });

        if (result.error) {
          console.error('Error bypassing clinician signature:', result.error);
          if (
            onOasisBlocked &&
            getConflictCode(result.error) === 'OASIS_CMS_FATAL_ERRORS'
          ) {
            setOpen(false);
            onOasisBlocked();
            return null;
          }
          const message =
            getBusinessErrorMessage(result) ??
            'Failed to bypass the signature. Please try again.';
          onError?.(message);
          return message;
        }

        await onBypassed();
        setOpen(false);
        return null;
      } catch (err) {
        console.error('Error bypassing clinician signature:', err);
        const message = 'Failed to bypass the signature. Please try again.';
        onError?.(message);
        return message;
      } finally {
        setIsLoading(false);
      }
    },
    [taskId, attestation, onBypassed, onError, onOasisBlocked]
  );

  const actionItems = useMemo<TaskHeaderSecondaryAction[]>(() => {
    if (!canBypass) return [];
    return [
      {
        label: 'Bypass clinician signature',
        icon: <ShieldAlert className='h-4 w-4 mr-2' aria-hidden='true' />,
        // Defer to the next frame so the Actions dropdown finishes unmounting
        // and releases its body pointer-events lock before the dialog mounts —
        // otherwise the page is left unclickable.
        onSelect: () => {
          if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
          frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;
            setOpen(true);
          });
        },
      },
    ];
  }, [canBypass]);

  // Mounted only for someone who could actually open it. `|| open` keeps it on
  // screen through the moment a successful bypass flips the status to completed
  // and revokes the permission underneath it.
  const dialog =
    canBypass || open ? (
      <BypassSignatureDialog
        open={open}
        attestation={attestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={isLoading}
        onConfirm={handleConfirm}
        onCancel={() => setOpen(false)}
      />
    ) : null;

  return { actionItems, dialog };
}
